import type { ApiClientV2 } from 'utils/api-client/api-client.utils'
import type { GraphitiError } from 'utils/api-client/api-client-base.types'
import type { VisaRead, VisaPayload } from 'shop/components/visa-block/visa.utils'

import { FromSwagger, FromSwaggerWithoutId } from 'types/helpers'
import {
  ShopPersonalisationAttendeeAttributes,
  ShopPersonalisationAttributes,
  ShopPersonalisationField,
} from 'types/__generated__/api'

export type Personalisation = FromSwagger<ShopPersonalisationAttributes>

// Presence of the visa key gates the block, not `enabled`.
export type PersonalisationAttendee = FromSwagger<ShopPersonalisationAttendeeAttributes> & {
  visa?: VisaRead
}

// Outbound `visa` is the write payload, never the read's masked echo.
export type PersonalisationAttendeeUpdate = Partial<Omit<PersonalisationAttendee, 'visa'>> & {
  visa?: VisaPayload
}

export type PersonalisationField = FromSwaggerWithoutId<ShopPersonalisationField>

export type PersonalisationErrorType =
  | 'not_found'
  | 'invalid_code'
  | 'rate_limited'
  | 'not_verified'
  | 'read_only'
  | 'captcha_failed'
  | 'invalid_email'
  | 'email_mismatch'
  | 'same_holder'
  | 'not_transferable_status'
  | 'not_transferable_state'
  | 'conflict'
  | 'temporarily_closed'
  | 'unexpected'

export type JsonApiError = Partial<Omit<GraphitiError, 'id' | 'status' | 'meta'>> & {
  status?: string | number
  meta?: NonNullable<GraphitiError['meta']> & { type?: PersonalisationErrorType }
}

// Refused requests often arrive with no envelope status (`withCamelCaseKeys` flattens it). Read it from the first error.
export type ApiResult<T> =
  | { data: T; included?: Array<{ id: string; type: string; attributes: Record<string, any> }>; status?: number }
  | { errors: JsonApiError[]; status?: number }

export const hasErrors = <T>(resp: ApiResult<T> | undefined): resp is { errors: JsonApiError[]; status?: number } =>
  Array.isArray((resp as { errors?: unknown } | undefined)?.errors) &&
  (resp as { errors: JsonApiError[] }).errors.length > 0

const normalise = <T>(resp: any): ApiResult<T> => {
  if (hasErrors<T>(resp)) {
    const raw = resp.status ?? resp.errors[0]?.status
    const status = raw === undefined || raw === null ? undefined : Number(raw)
    return { ...resp, status: Number.isNaN(status) ? undefined : status } as ApiResult<T>
  }

  return resp as ApiResult<T>
}

const BASE_URL = (username: string, token: string) => `shop/${username}/personalisations/${token}`

// `type` and `meta` are reserved keys. Grants travel in document `meta`, never the query string.
export const createPersonalisationsApi = ({ GET, POST, PATCH }: ApiClientV2) => ({
  fetchItem: (username: string, token: string) =>
    GET<Personalisation>(BASE_URL(username, token)).then((resp) => normalise<Personalisation>(resp)),

  verify: (username: string, token: string, code: string, recaptcha?: string) =>
    POST<Personalisation>(`${BASE_URL(username, token)}/verify`, {
      code,
      ...(recaptcha ? { meta: { recaptcha } } : {}),
    }).then((resp) => normalise<Personalisation>(resp)),

  updateAttendee: (
    username: string,
    token: string,
    attendee: PersonalisationAttendeeUpdate,
    verificationToken?: string
  ) =>
    PATCH<Personalisation>(BASE_URL(username, token), {
      type: 'personalisation_attendees',
      ...attendee,
      ...(verificationToken ? { meta: { verificationToken } } : {}),
    }).then((resp) => normalise<Personalisation>(resp)),

  // Transfer success is 204 with no body (token rotated). Success is "no errors".
  transfer: (
    username: string,
    token: string,
    details: { email: string; emailConfirmation: string; code: string },
    verificationToken?: string,
    recaptcha?: string
  ) => {
    const meta = {
      ...(verificationToken ? { verificationToken } : {}),
      ...(recaptcha ? { recaptcha } : {}),
    }

    return POST<null>(`${BASE_URL(username, token)}/transfer`, {
      email: details.email,
      emailConfirmation: details.emailConfirmation,
      code: details.code,
      ...(Object.keys(meta).length ? { meta } : {}),
    }).then((resp) => normalise<null>(resp))
  },
})

export type PersonalisationsApi = ReturnType<typeof createPersonalisationsApi>
